import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { X, Pencil, Check, Copy, Zap, RefreshCw } from 'lucide-react';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';
import { TraitStyleConfig, AttributeStyleConfig, IconStyleId, DeviceModalConfig } from '../../types';

// Import API for self-fetching energy data
import { ultronTuringApi } from '../../../../services/ultronTuringApi';

export interface ThreePhaseDevice {
  deviceId: string;
  baseSN?: string;
  deviceName?: string;
  nickname?: string;
  location?: string;
  groupName?: string;
  isOnline?: boolean;
  online?: boolean;  // Alternative online status field (from dashboard)
  status?: 'online' | 'offline';  // Status from device
  // Total values
  watt?: number;
  voltage?: number;
  current?: number;
  wattHour?: number;
  energy?: number;
  // Three-phase data (from attributes)
  voltage_r?: number;
  voltage_s?: number;
  voltage_t?: number;
  current_r?: number;
  current_s?: number;
  current_t?: number;
  power_r?: number;
  power_s?: number;
  power_t?: number;
  real_power_r?: number;
  real_power_s?: number;
  real_power_t?: number;
  traits?: string[];
  attributes?: Record<string, unknown>;
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
}

export interface ThreePhaseModalProps {
  device: ThreePhaseDevice;
  onClose: () => void;
  onSaveNickname?: (device: ThreePhaseDevice, nickname: string | null) => Promise<void>;
  config?: Partial<DeviceModalConfig>;
  isMergedDevice?: boolean;
  onToggleMerge?: (baseSN: string) => void;
  // 24H Energy chart data (optional - component will self-fetch if not provided)
  energyChartData?: Array<{ time: string; value: number }>;
  isLoadingEnergyChart?: boolean;
}

interface PhaseData {
  name: string;
  color: string;
  bgColor: string;
  borderColor: string;
  voltage: number;
  current: number;
  power: number;
  isActive: boolean;
}

/**
 * PhaseCard Component - Individual phase display card
 */
const PhaseCard: React.FC<{ phase: PhaseData }> = ({ phase }) => (
  <div className={`flex-1 bg-slate-800/30 rounded-xl p-3 border ${phase.borderColor} text-center`}>
    {/* Phase header */}
    <div className="flex items-center justify-center gap-2 mb-3">
      <span className={`w-5 h-5 rounded-full ${phase.bgColor} flex items-center justify-center text-white text-xs font-bold`}>
        {phase.name}
      </span>
      <span className="text-white text-sm font-medium">Phase {phase.name}</span>
    </div>
    
    {/* Phase metrics */}
    <div className="space-y-2">
      <div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">VOLTAGE</span>
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-lg font-bold text-white">{phase.voltage.toFixed(1)}</span>
          <span className="text-xs text-slate-400">V</span>
        </div>
      </div>
      <div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">CURRENT</span>
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-lg font-bold text-white">{phase.current.toFixed(2)}</span>
          <span className="text-xs text-slate-400">A</span>
        </div>
      </div>
      <div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">POWER</span>
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-lg font-bold text-white">{phase.power.toFixed(1)}</span>
          <span className="text-xs text-slate-400">W</span>
        </div>
      </div>
    </div>
  </div>
);

/**
 * PowerMeter3Phase Modal Template
 * 
 * 三相電錶顯示 Modal (只讀，無控制)
 * - 總功率 (W/kW) 圓形顯示
 * - 總電壓 (V) + 總電流 (A)
 * - R/S/T 三相數據並排
 * - 過去24小時用電量圖表
 */
export const Template01_Dashboard: React.FC<ThreePhaseModalProps> = ({
  device,
  onClose,
  onSaveNickname,
  config,
  isMergedDevice = false,
  onToggleMerge,
  energyChartData = [],
  isLoadingEnergyChart = false,
}) => {
  // ✅ 快照：modal 打開時保存設備數據，之後不再更新（只讀）
  // 使用 useState 初始化函數確保只在首次掛載時執行一次
  const [deviceSnapshot] = useState(() => JSON.parse(JSON.stringify(device)) as typeof device);
  
  // ✅ 自行獲取能源圖表數據 - 在 modal 打開時觸發
  // 這樣可以確保每次打開 modal 都是獲取該設備的數據，不會有 race condition
  const [selfFetchedChartData, setSelfFetchedChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [isSelfLoading, setIsSelfLoading] = useState(false);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    
    const fetchEnergyData = async () => {
      const deviceSN = device.deviceId;
      console.log(`📊 [3Phase Modal] Fetching energy data for: ${deviceSN}`);
      
      setIsSelfLoading(true);
      setSelfFetchedChartData([]); // Clear old data immediately
      
      try {
        const report = await ultronTuringApi.generateReport([deviceSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',
          utcOffset: '+8:00'
        });
        
        // Check if component is still mounted
        if (!mountedRef.current) {
          console.log(`📊 [3Phase Modal] Component unmounted, ignoring response for: ${deviceSN}`);
          return;
        }
        
        if (report.success && report.data) {
          const reportData = report.data as Record<string, any>;
          const deviceData = reportData[deviceSN];
          
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            const chartData = deviceData.chartData
              .map((point: any) => ({
                time: point.time ? formatTimeLabel(point.time) : '',
                value: point.energy ?? 0
              }))
              .sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());
            
            console.log(`📊 [3Phase Modal] ✅ Got ${chartData.length} data points for: ${deviceSN}`);
            setSelfFetchedChartData(chartData);
          } else {
            console.log(`📊 [3Phase Modal] ❌ No chartData found for: ${deviceSN}`);
          }
        }
      } catch (error) {
        console.error(`📊 [3Phase Modal] Error fetching energy data:`, error);
      } finally {
        if (mountedRef.current) {
          setIsSelfLoading(false);
        }
      }
    };
    
    // Helper to format time label
    const formatTimeLabel = (timeStr: string) => {
      const d = new Date(timeStr);
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const hour = d.getHours().toString().padStart(2, '0');
      return `${month}/${day} ${hour}:00`;
    };
    
    fetchEnergyData();
    
    // Cleanup: mark as unmounted to ignore stale responses
    return () => {
      mountedRef.current = false;
    };
  }, [device.deviceId]); // Only re-fetch if deviceId changes
  
  // Use self-fetched data if available, otherwise fallback to prop
  const chartDataToUse = selfFetchedChartData.length > 0 ? selfFetchedChartData : energyChartData;
  const isChartLoading = isSelfLoading || isLoadingEnergyChart;
  
  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];
  // Unified order: Attributes (BigIcon is always fixed at top)
  const traitOrder: string[] = config?.traitOrder || ['SensorDisplay', 'SensorChart'];
  
  // Nickname editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(device.nickname || device.deviceName || '');
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Sync editedName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && !isSavingRef.current && Date.now() > blockSyncUntilRef.current) {
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  // Extract three-phase data from snapshot (frozen at modal open time)
  // Data can be in: device.attributes, device.attributes.values, device.attributes["1"], or directly on device
  const phaseData = useMemo(() => {
    const getPhaseValue = (field: string): number => {
      const rawAttrs = deviceSnapshot.attributes || {};
      // ✅ 新增：檢查 attributes.values 路徑（QueryStatus 回傳格式）
      const valuesObj = (rawAttrs as Record<string, unknown>)?.values as Record<string, unknown> | undefined;
      
      // 1. Check device direct properties first
      const deviceValue = (deviceSnapshot as unknown as Record<string, unknown>)[field];
      if (typeof deviceValue === 'number') return deviceValue;
      
      // 2. ✅ Check attributes.values[field].value[0] (QueryStatus format)
      if (valuesObj) {
        const valuesField = valuesObj[field];
        if (valuesField && typeof valuesField === 'object') {
          const objVal = (valuesField as Record<string, unknown>).value;
          if (Array.isArray(objVal) && objVal.length > 0 && typeof objVal[0] === 'number') {
            return objVal[0];
          }
          if (typeof objVal === 'number') return objVal;
        }
      }
      
      // 3. Check attributes root level (after getArrayVal processing, stored as numbers)
      const rootAttrValue = (rawAttrs as Record<string, unknown>)[field];
      if (typeof rootAttrValue === 'number') return rootAttrValue;
      
      // 4. Check nested under "1" (endpoint key)
      const nestedAttrs = (rawAttrs as Record<string, unknown>)?.["1"];
      if (nestedAttrs && typeof nestedAttrs === 'object') {
        const nestedValue = (nestedAttrs as Record<string, unknown>)[field];
        if (typeof nestedValue === 'number') return nestedValue;
        // Handle { value: [number] } format
        if (typeof nestedValue === 'object' && nestedValue !== null) {
          const objVal = (nestedValue as Record<string, unknown>).value;
          if (Array.isArray(objVal) && objVal.length > 0 && typeof objVal[0] === 'number') {
            return objVal[0];
          }
        }
      }
      
      // 5. Handle { value: [number] } format at root level
      if (typeof rootAttrValue === 'object' && rootAttrValue !== null) {
        const objVal = (rootAttrValue as Record<string, unknown>).value;
        if (Array.isArray(objVal) && objVal.length > 0 && typeof objVal[0] === 'number') {
          return objVal[0];
        }
        if (typeof objVal === 'number') return objVal;
      }
      
      return 0;
    };

    return {
      voltage_r: getPhaseValue('voltage_r'),
      voltage_s: getPhaseValue('voltage_s'),
      voltage_t: getPhaseValue('voltage_t'),
      current_r: getPhaseValue('current_r'),
      current_s: getPhaseValue('current_s'),
      current_t: getPhaseValue('current_t'),
      real_power_r: getPhaseValue('real_power_r'),
      real_power_s: getPhaseValue('real_power_s'),
      real_power_t: getPhaseValue('real_power_t'),
    };
  }, [deviceSnapshot]);

  // Helper to convert mA to A (API may return current in mA)
  const toAmperes = (value: number): number => value > 100 ? value / 1000 : value;

  // Phase data - using memoized values to prevent re-renders
  const phaseR: PhaseData = useMemo(() => ({
    name: 'R',
    color: 'red',
    bgColor: 'bg-red-500',
    borderColor: 'border-red-500/30',
    voltage: phaseData.voltage_r,
    current: toAmperes(phaseData.current_r),
    power: phaseData.real_power_r,
    isActive: phaseData.real_power_r > 0,
  }), [phaseData]);

  const phaseS: PhaseData = useMemo(() => ({
    name: 'S',
    color: 'yellow',
    bgColor: 'bg-yellow-500',
    borderColor: 'border-yellow-500/30',
    voltage: phaseData.voltage_s,
    current: toAmperes(phaseData.current_s),
    power: phaseData.real_power_s,
    isActive: phaseData.real_power_s > 0,
  }), [phaseData]);

  const phaseT: PhaseData = useMemo(() => ({
    name: 'T',
    color: 'blue',
    bgColor: 'bg-blue-500',
    borderColor: 'border-blue-500/30',
    voltage: phaseData.voltage_t,
    current: toAmperes(phaseData.current_t),
    power: phaseData.real_power_t,
    isActive: phaseData.real_power_t > 0,
  }), [phaseData]);

  // Total values (from snapshot - frozen at modal open time)
  const totalPower = deviceSnapshot.watt ?? (phaseR.power + phaseS.power + phaseT.power);
  const totalVoltage = deviceSnapshot.voltage ?? ((phaseR.voltage + phaseS.voltage + phaseT.voltage) / 3);
  // Convert deviceSnapshot.current from mA to A if needed, or sum from phases (already converted)
  const rawTotalCurrent = deviceSnapshot.current ?? (phaseR.current + phaseS.current + phaseT.current);
  const totalCurrent = deviceSnapshot.current ? toAmperes(deviceSnapshot.current) : rawTotalCurrent;

  const displayName = device.nickname || device.deviceName || device.deviceId;
  const displaySN = isMergedDevice ? device.baseSN : device.deviceId;

  // Multi-channel check (from snapshot)
  const isMultiChannel = deviceSnapshot.system?.endpointRelationship?.classification === 'multi_channel';

  // Format power display (W or kW)
  const formatPower = (watts: number): { value: string; unit: string } => {
    if (watts >= 1000) {
      return { value: (watts / 1000).toFixed(2), unit: 'kW' };
    }
    return { value: watts.toFixed(1), unit: 'W' };
  };

  const powerDisplay = formatPower(totalPower);

  // Handle nickname save
  const handleSaveNickname = useCallback(async () => {
    if (onSaveNickname && editedName !== (device.nickname || device.deviceName)) {
      isSavingRef.current = true;
      blockSyncUntilRef.current = Date.now() + 3000;
      await onSaveNickname(device, editedName || null);
      isSavingRef.current = false;
    }
    setIsEditingName(false);
  }, [device, editedName, onSaveNickname]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveNickname();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    }
  }, [handleSaveNickname, device.nickname, device.deviceName]);

  // Copy SN to clipboard
  const handleCopySN = useCallback(() => {
    navigator.clipboard.writeText(displaySN || '');
  }, [displaySN]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setEditedName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="power_meter_3phase" 
              isOn={totalPower > 0}
              size={20}
              styleId={smallIconStyle}
            />
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="bg-slate-800 text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate">{displayName}</span>
            )}
            {isEditingName ? (
              <button onClick={handleSaveNickname} className="text-blue-400 hover:text-blue-300">
                <Check className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setIsEditingName(true)} className="text-slate-400 hover:text-white">
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                // ✅ Check multiple online status fields: isOnline, online, or status
                online: deviceSnapshot.isOnline ?? deviceSnapshot.online ?? deviceSnapshot.status === 'online',
                label: (deviceSnapshot.isOnline ?? deviceSnapshot.online ?? deviceSnapshot.status === 'online') ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon - Total power display (circular) */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center py-4">
              <div className="w-32 h-32 rounded-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-2 border-blue-400/50 shadow-lg shadow-blue-500/30">
                <DeviceIcon
                  deviceType="power_meter_3phase"
                  isOn={true}
                  size={28}
                  styleId={bigIconStyle}
                  className="text-blue-400 mb-1"
                />
                <span className="text-3xl font-bold text-white">{powerDisplay.value}</span>
                <span className="text-base text-blue-300">{powerDisplay.unit}</span>
              </div>
            </div>
          )}

          {/* Dynamically rendered components based on traitOrder */}
          {traitOrder.map((componentName) => {
            if (hiddenComponents.includes(componentName)) return null;
            
            switch (componentName) {
              case 'SensorDisplay':
                return (
                  <div key="SensorDisplay" className="space-y-3">
                    {/* Total voltage and current */}
                    <div className="grid grid-cols-2 gap-3">
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          value: totalVoltage,
                          type: 'voltage',
                          label: '電壓',
                          unit: 'V',
                          precision: 1
                        }}
                      />
                      <DynamicAttribute
                        attributeName="SensorDisplay"
                        styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                        componentProps={{
                          value: totalCurrent,
                          type: 'current',
                          label: '電流',
                          unit: 'A',
                          precision: 2
                        }}
                      />
                    </div>
                    {/* Real-time Phase Data section */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-slate-300">Real-time Phase Data</h4>
                      <div className="flex gap-2">
                        <PhaseCard phase={phaseR} />
                        <PhaseCard phase={phaseS} />
                        <PhaseCard phase={phaseT} />
                      </div>
                    </div>
                  </div>
                );
              
              case 'SensorChart':
                return (
                  <DynamicAttribute
                    key="SensorChart"
                    attributeName="SensorChart"
                    styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                    componentProps={{
                      data: chartDataToUse.map(d => ({
                        time: d.time,
                        value: d.value
                      })),
                      title: '過去 24 小時用電量',
                      color: '#3b82f6',
                      unit: ' kWh',
                      height: 120,
                      loading: isChartLoading
                    }}
                  />
                );
              
              default:
                return null;
            }
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-800/50 space-y-3">
          {/* Multi-channel toggle */}
          {isMultiChannel && onToggleMerge && device.baseSN && (
            <div className="flex items-center justify-center gap-3 py-2">
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  isMergedDevice 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                單一方塊
              </button>
              <button
                onClick={() => onToggleMerge(device.baseSN!)}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  !isMergedDevice 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                個別方塊
              </button>
            </div>
          )}

          {/* Location and SN */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{deviceSnapshot.location || deviceSnapshot.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{displaySN}</span>
              <button onClick={handleCopySN} className="text-slate-400 hover:text-white">
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Dashboard;
